import { ErrorPage } from '@everfi/reactifi';
import {
  AuthorizationNotifier,
  AuthorizationServiceConfiguration,
  DefaultCrypto,
  FetchRequestor,
  GRANT_TYPE_AUTHORIZATION_CODE,
  LocalStorageBackend,
  RedirectRequestHandler,
  TokenRequest,
} from '@openid/appauth';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import environment from '../environment';
import Fallback from '../layout/FallBack';
import { CustomTokenRequestHandler } from './CustomTokenHandler';
import { useTokenStore } from './TokenStore';
import { NoHashQueryStringUtils } from './noHashQueryStringUtils';
const { getAuthApp } = environment;

const Callback = ({ app, fallbackUserType = 'Default' }) => {
  const tokenStore = useTokenStore();
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const authApp = useMemo(() => getAuthApp(app), [app]);
  const [authenticated, setAuthenticated] = useState('waiting');

  useEffect(() => {
    const processCode = async () => {
      const tokenHandler = new CustomTokenRequestHandler(new FetchRequestor());
      const authorizationHandler = new RedirectRequestHandler(
        new LocalStorageBackend(),
        new NoHashQueryStringUtils(),
        window.location,
        new DefaultCrypto()
      );
      const notifier = new AuthorizationNotifier();
      authorizationHandler.setAuthorizationNotifier(notifier);
      notifier.setAuthorizationListener(async (request, response, error) => {
        if (response) {
          const extras = request && request.internal ? { code_verifier: request.internal.code_verifier } : null;
          const tokenRequest = new TokenRequest({
            client_id: authApp?.clientId,
            redirect_uri: authApp?.redirectUri,
            grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
            code: response.code,
            refresh_token: undefined,
            extras,
          });

          try {
            const configuration = await AuthorizationServiceConfiguration.fetchFromIssuer(
              authApp?.url,
              new FetchRequestor()
            );
            const oResponse = await tokenHandler.performTokenRequest(configuration, tokenRequest);
            tokenStore.storeToken(oResponse.accessToken, oResponse.ownerType || fallbackUserType);
            const destination = localStorage.getItem('original-login-destination');
            const safeDestination = destination && destination.startsWith('/') ? destination : '/';
            navigate(safeDestination, { replace: true });
          } catch (oError) {
            setError(oError);
          }
        }
      });

      await authorizationHandler.completeAuthorizationRequestIfPossible();
      setTimeout(() => setError('Your login may be stale, please try again'), 8000);
    };
    const codeParam = searchParams.get('code');

    if (codeParam && authenticated === 'pending') {
      processCode();
    }
  }, [authenticated, authApp, navigate, searchParams, tokenStore, fallbackUserType]);

  useEffect(() => {
    const codeParam = searchParams.get('code');
    if (authenticated === 'waiting' && codeParam) {
      setAuthenticated('pending');
    }
  }, [authenticated, searchParams, tokenStore]);

  const errorMessage = [
    t('We had trouble authenticating you, please try refreshing the page or go back to the login page to try again'),
  ];
  if (error) {
    errorMessage.push(`Error Message: ${error}`);
    errorMessage.push(<Link to={localStorage.getItem('original-login-destination')}>{t('Try Again')}</Link>);
  }

  return <Fallback>{error && <ErrorPage errors={errorMessage} title={t('Cannot authenticate')} />}</Fallback>;
};

export default Callback;
